import useAlert from "@hooks/AlertHook"
import { useState } from "react"

type AttributeInputsProp = {
    attributes: Attribute[],
    onChange?: (attributes: Attribute[]) => void
}

type Attribute = {
    type: string,
    value: string
}

export function AttributeInputs({ onChange, attributes }: AttributeInputsProp) {

    const alert = useAlert()
    const [type, setType] = useState("")
    const [value, setValue] = useState("")
    const [index, setIndex] = useState<number>()

    const onAdd = () => {
        try {
            if (!type) throw new Error("Attribute name cannot be empty")
            if (!value) throw new Error("Attribute value cannot be empty")

            if (!index) attributes.push({ type, value })
            else attributes.splice(index, 1, { type, value })
            onChange?.call(undefined, attributes)

            setType("")
            setValue("")
            setIndex(undefined)
        } catch (e) {
            if (e instanceof Error) alert.failed(e.message)
        }
    }

    const onDelete = (index: number) => {
        attributes.splice(index, 1)
        onChange?.call(undefined, [...attributes])
    }

    const onUpdate = (index: number) => {
        setIndex(index)
        setType(attributes[index].type)
        setValue(attributes[index].value)
    }

    return <>
        {
            attributes.map((attribute, index) => {
                return <div
                    key={index}
                    className="flex flex-shrink-0 items-center mt-1 text-xs font-medium text-jacarta-500 dark:text-jacarta-300 sm:text-sm">
                    <div
                        className="flex items-center w-full text-xs font-medium text-jacarta-500 dark:text-jacarta-300 sm:text-sm">
                        <div
                            className="flex h-10 w-full items-center border-r-0 border border-jacarta-100 bg-white p-3 first:rounded-l-lg last:rounded-r-lg dark:border-gray-400 dark:bg-gray-900 text-white sm:px-4 sm:py-2">
                            {attribute.type}
                        </div>
                        <div
                            className="flex h-10 w-full items-center whitespace-nowrap border border-jacarta-100 bg-white p-3 first:rounded-l-lg last:rounded-r-lg dark:border-gray-400 dark:bg-gray-900 text-white sm:px-4 sm:py-2">
                            {attribute.value}
                        </div>
                    </div>
                    {
                        onChange &&
                        <div
                            className="ml-2 flex items-center text-xs font-medium text-jacarta-500 dark:text-jacarta-300 sm:text-sm">
                            <button
                                onClick={() => onUpdate(index)}
                                className="flex h-10 w-10 group cursor-pointer items-center justify-center border-r-0 first:rounded-l-lg last:rounded-r-lg dark:bg-gray-900 dark:border-gray-400 border border-jacarta-100 bg-white dark:hover:bg-jacarta-500 hover:bg-accent dark:hover:border-transparent">
                                <svg className="h-4 w-4 fill-jacarta-700 dark:fill-gray-400 group-hover:fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path d="M1.172,19.119A4,4,0,0,0,0,21.947V24H2.053a4,4,0,0,0,2.828-1.172L18.224,9.485,14.515,5.776Z" /><path d="M23.145.855a2.622,2.622,0,0,0-3.71,0L15.929,4.362l3.709,3.709,3.507-3.506A2.622,2.622,0,0,0,23.145.855Z" /></svg>
                            </button>
                            <button
                                onClick={() => onDelete(index)}
                                className="flex h-10 w-10 group cursor-pointer items-center justify-center first:rounded-l-lg last:rounded-r-lg dark:bg-gray-900 dark:border-gray-400 border border-jacarta-100 bg-white dark:hover:bg-jacarta-500 dark:hover:border-transparent hover:bg-accent">
                                <svg className="h-4 w-4 fill-jacarta-700 dark:fill-gray-400 group-hover:fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path d="M23,3H18V2.5A2.5,2.5,0,0,0,15.5,0h-7A2.5,2.5,0,0,0,6,2.5V3H1V6H3V21a3,3,0,0,0,3,3H18a3,3,0,0,0,3-3V6h2ZM18,21H6V6H18Z" /><rect x="8" y="9" width="3" height="9" /><rect x="13" y="9" width="3" height="9" /></svg>
                            </button>
                        </div>
                    }
                </div>
            })
        }
        {
            onChange &&
            <div
                className={`flex flex-shrink-0 items-center ${attributes.length === 0 ? '' : 'mt-5'} text-xs font-medium text-jacarta-500 dark:text-jacarta-300 sm:text-sm`}>
                <div
                    className="flex items-center w-full text-xs font-medium sm:text-sm mr-2">
                    <input
                        value={type}
                        onChange={({ target }) => setType(target.value)}
                        placeholder="Name"
                        className="placeholder-gray-400 text-white flex h-10 w-full items-center border border-jacarta-100 bg-white p-3 first:rounded-l-lg last:rounded-r-lg dark:border-gray-400 dark:bg-gray-900 sm:px-4 sm:py-2 hover:ring-2 hover:ring-jacarta-500/10 focus:ring-jacarta-500" />
                </div>
                <div
                    className="flex items-center w-full text-xs font-medium sm:text-sm mr-2">
                    <input
                        value={value}
                        onChange={({ target }) => setValue(target.value)}
                        placeholder="Value"
                        className="placeholder-gray-400 flex h-10 w-full items-center text-white whitespace-nowrap border border-gray-400 bg-white p-3 first:rounded-l-lg last:rounded-r-lg dark:border-gray-400 dark:bg-gray-900 sm:px-4 sm:py-2 hover:ring-2 hover:ring-jacarta-500/10 focus:ring-jacarta-500" />
                </div>
                <div
                    className="flex items-center text-xs font-medium text-jacarta-500 dark:text-jacarta-300 sm:text-sm">
                    <button
                        onClick={onAdd}
                        className="flex h-10 w-10 group cursor-pointer items-center justify-center first:rounded-l-lg last:rounded-r-lg dark:bg-gray-900 dark:border-gray-400 border border-jacarta-100 bg-white dark:hover:bg-jacarta-500 hover:bg-jacarta-500 dark:hover:border-transparent">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width="24"
                            height="24"
                            className="fill-gray-400 group-hover:fill-white"
                        >
                            <path fill="none" d="M0 0h24v24H0z" />
                            <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z" />
                        </svg>
                    </button>
                </div>
            </div>
        }
    </>
}